/**
 * Patient List by Practitioner API service — 患者リスト(施術師毎) / /visits/visitpatient_init.
 */

import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import type {
  VisitPatientSearchParams,
  VisitPatientSearchResponse,
  VisitPatientMassagersResponse,
  VisitPatientInsurerTypesResponse,
  PrintVisitPatientPayload,
  ExportVisitPatientCsvPayload,
} from '@/pages/main/visitPatient/types';

function toParams(
  filters: VisitPatientSearchParams
): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params[key] = value as string | number;
    }
  });
  return params;
}

export async function searchVisitPatients(
  siteType: string,
  clinicNumber: string,
  filters: VisitPatientSearchParams,
  serviceType: string = SERVICE_TYPE_A2
): Promise<VisitPatientSearchResponse> {
  const response = await apiClient.get<VisitPatientSearchResponse>(
    clinicApiPath(siteType, clinicNumber, '/visits/visit-patients/search'),
    {
      params: toParams(filters),
      headers: { 'X-Service-Type': serviceType },
    }
  );
  return response.data;
}

export async function getVisitPatientMassagers(
  siteType: string,
  clinicNumber: string,
  serviceType: string = SERVICE_TYPE_A2,
  targetDate?: string | null
): Promise<VisitPatientMassagersResponse> {
  const response = await apiClient.get<VisitPatientMassagersResponse>(
    clinicApiPath(siteType, clinicNumber, '/visits/visit-patients/massagers'),
    {
      params: targetDate ? { target_date: targetDate } : undefined,
      headers: { 'X-Service-Type': serviceType },
    }
  );
  return response.data;
}

export async function getVisitPatientInsurerTypes(
  siteType: string,
  clinicNumber: string,
  serviceType: string = SERVICE_TYPE_A2
): Promise<VisitPatientInsurerTypesResponse> {
  const response = await apiClient.get<VisitPatientInsurerTypesResponse>(
    clinicApiPath(siteType, clinicNumber, '/visits/visit-patients/insurer-types'),
    {
      headers: { 'X-Service-Type': serviceType },
    }
  );
  return response.data;
}

export async function printVisitPatients(
  siteType: string,
  clinicNumber: string,
  payload: PrintVisitPatientPayload,
  serviceType: string = SERVICE_TYPE_A2
): Promise<Blob> {
  const response = await apiClient.post(
    clinicApiPath(siteType, clinicNumber, '/visits/visit-patients/print'),
    payload,
    {
      responseType: 'blob',
      headers: { 'X-Service-Type': serviceType },
    }
  );
  return response.data as Blob;
}

export async function exportVisitPatientsCsv(
  siteType: string,
  clinicNumber: string,
  payload: ExportVisitPatientCsvPayload,
  serviceType: string = SERVICE_TYPE_A2
): Promise<Blob> {
  const response = await apiClient.post(
    clinicApiPath(siteType, clinicNumber, '/visits/visit-patients/export-csv'),
    payload,
    {
      responseType: 'blob',
      headers: { 'X-Service-Type': serviceType },
    }
  );
  return response.data as Blob;
}
